import { useEffect, useState } from 'react'

import {
  IUploadLog,
  IUploadLogAggregated,
  IUploadLogAggregationFilterInput,
} from '~/client/graph'
import { useGetUploadLogAggregationListQuery } from '~/client/graph/operations/generated/FileUploader.generated'
import Workflow from '~/client/src/shared/models/Workflow'

const defaultPagination: IPagination = {
  offset: 0,
  limit: 1_000_000,
}

interface IPagination {
  offset: number
  limit: number
}

export default function useUploadLogs(
  projectId: string,
  filters: IUploadLogAggregationFilterInput,
  pagination: IPagination = defaultPagination,
) {
  const [uploadLogs, setUploadLogs] = useState<IUploadLog[]>([])
  const [workflows, setWorkflows] = useState<Map<string, Workflow>>(new Map())
  const [aggregatedData, setAggregatedData] = useState<IUploadLogAggregated[]>(
    [],
  )

  const {
    data: uploadLogsData,
    loading: uploadLogsLoading,
    error,
  } = useGetUploadLogAggregationListQuery({
    variables: {
      filter: {
        projectId,
        ...filters,
      },
      limit: pagination.limit,
      offset: pagination.offset,
    },
    skip: !projectId,
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    const data = uploadLogsData?.uploadLogsAggregation?.data || []
    setAggregatedData(data as IUploadLogAggregated[])

    const extractedUploadLogs = data
      .map(aggregated => aggregated.uploadLog)
      .filter(Boolean) as IUploadLog[]
    setUploadLogs(extractedUploadLogs)

    const workflowsMap = new Map<string, Workflow>()
    data.forEach(aggregated => {
      if (aggregated.sitePermit && aggregated.uploadLog?.sourceEntityId) {
        try {
          const workflowDto = {
            ...aggregated.sitePermit,
            projectId: aggregated.uploadLog.projectId || '',
          } as any

          const workflow = Workflow.fromDto(workflowDto)
          workflowsMap.set(aggregated.uploadLog.sourceEntityId, workflow)
        } catch (error) {
          console.warn(
            'Failed to create SitePermit from aggregated data:',
            error,
          )
        }
      }
    })
    setWorkflows(workflowsMap)
  }, [uploadLogsData, error, projectId])

  return {
    uploadLogs,
    workflows,
    aggregatedData,
    uploadLogsLoading,
  }
}
